import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/firestore';

import { FinalizeUserCreationError } from '../../errors';
import { User } from '../../models';

import { UsersManagementService } from '../users-management.service';
import { FirestoreService } from './firestore.service';

@Injectable()
export class FirestoreUsersManagementService extends UsersManagementService {
    constructor(
        private firestore: AngularFirestore,
        private firestoreService: FirestoreService,
    ) {
        super();
    }

    async verifyInviteTokenValidity(token: string): Promise<{ valid: true; user: User } | { valid: false; error: 'Invalid' | 'Expired' }> {
        const verifyNewUserInviteTokenValidity = this.firestoreService.cloudFunction('verifyNewUserInviteTokenValidity');
        const res = await verifyNewUserInviteTokenValidity({ token });

        if (res && res.data) {
            // verifies if it is valid
            if (res.data.valid) {
                return { valid: true, user: res.data.user };
            }

            // otherwise is invalid
            // return the right error

            if (res.data.error === 'Expired') {
                return {
                    valid: false,
                    error: 'Expired',
                };
            }

            if (res.data.error === 'NotFound') {
                return {
                    valid: false,
                    error: 'Invalid',
                };
            }
        }

        return {
            valid: false,
            error: 'Invalid',
        };
    }

    async finalizeUserCreation(token: string, password: string, confirmationPassword: string): Promise<void> {
        // verify if new and confirmation password are the same
        if (password && password && password !== password) {
            throw new FinalizeUserCreationError('passwordAndConfirmationPasswordDoesNotMatch', 'The current password is invalid');
        }

        const finalizeUserCreation = this.firestoreService.cloudFunction('finalizeUserCreation');
        const res = await finalizeUserCreation({ token, password, confirmationPassword });

        if (res && res.data) {
            switch (res.data.code) {
                case 'deadline-exceeded':
                    throw new FinalizeUserCreationError('expiredToken', 'The token has expired.');
                case 'invalid-argument':
                case 'invalid-argument': {
                    switch (res.data.details) {
                        case 'confirmation-password-dont-match-password':
                            throw new FinalizeUserCreationError(
                                'passwordAndConfirmationPasswordDoesNotMatch',
                                "The password and confirmation password don't match."
                            );
                        case 'password-insecure':
                            throw new FinalizeUserCreationError(
                                'passwordInsecure',
                                "the password don't have the necessary length/chars to be considered secure"
                            );
                        case 'password-contains-used-data':
                            throw new FinalizeUserCreationError(
                                'passwordContainsUserData',
                                'the password contains segments that match some user data (name / email)'
                            );
                    }
                    break;
                }
                default:
                    throw new FinalizeUserCreationError('unknown', 'An unknown error occurred.');
            }
        }
    }
}
